import { Printer, X, QrCode } from '../../lib/icons';
import { useEffect, useState } from 'react';
import { getBranding } from '../../lib/branding';
import { fmt } from '../shared/badges';
import { couleurGain, couleurLigneTiers } from '../../lib/couleurs';
import { QrLien } from '../print/QrLien';
import { lienCompte } from '../../lib/portail';
import { publierCompte, fetchLotsFournisseur, fetchRelevesClients, tiersAvecCode } from '../../lib/dataProvider';
import { PeriodeFiltre, dansPeriode } from '../shared/PeriodeFiltre';
import { QrSoldeDialog } from './QrSoldeDialog';
import { SoldesSepares } from './SoldesSepares';

const th = 'border border-slate-300 px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-600';
const td = 'border border-slate-200 px-2 py-1.5 text-[11px] text-slate-800';

export const GrandLivreTiers = ({ compte, onClose }) => {
  const b = getBranding();
  const libelleTiers = tiersAvecCode(compte.tiersId);
  const solde = compte.solde;
  const [lien, setLien] = useState('');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [lots, setLots] = useState([]);
  const [huile, setHuile] = useState(null);
  const [qrSolde, setQrSolde] = useState(false);
  const lignesVisibles = compte.lignes.filter((l) => dansPeriode(l.date, du, au));

  // QR permanent du tiers : republication de l'instantané à jour.
  useEffect(() => {
    setLien(lienCompte(compte.tiersId));
    publierCompte(compte.tiersId);
  }, [compte.tiersId]);
  useEffect(() => {
    fetchLotsFournisseur(compte.tiersId, du, au).then(setLots);
  }, [compte.tiersId, du, au]);
  useEffect(() => {
    fetchRelevesClients().then((rs) =>
      setHuile(rs.find((r) => r.tiersId === compte.tiersId || r.nom === compte.nom) ?? null)
    );
  }, [compte.tiersId, compte.nom]);

  return (
    <div data-testid="grand-livre-overlay" className="fixed inset-0 z-[60] overflow-auto bg-slate-900/60 p-4 print:static print:bg-white print:p-0">
      <div className="mx-auto w-full max-w-4xl bg-white print:max-w-none">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 print:hidden">
          <p className="font-heading text-sm font-bold text-slate-900">Relevé de compte — {libelleTiers}</p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="grand-livre-csv"
              onClick={() => {
                const entetes = ['Date', 'Operation', 'Piece', 'Debit', 'Credit', 'Solde', 'Notes'];
                const lignes = compte.lignes.map((l) =>
                  [l.date, l.type, l.bon || '', fmt(l.debit, 3), fmt(l.credit, 3), fmt(l.solde, 3), (l.notes || '').replace(/;/g, ',')].join(';')
                );
                const blob = new Blob([[entetes.join(';'), ...lignes].join('\n')], { type: 'text/csv;charset=utf-8;' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `releve-${compte.nom.replace(/\s+/g, '-').toLowerCase()}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
              }}
              className="flex h-10 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              Export CSV
            </button>
            <button
              type="button"
              data-testid="grand-livre-qr-solde"
              onClick={() => setQrSolde(true)}
              className="flex h-10 items-center gap-2 border border-emerald-700 px-4 text-sm font-bold text-emerald-800 transition-colors duration-150 hover:bg-emerald-700 hover:text-white"
            >
              <QrCode size={15} /> QR code de solde
            </button>
            <button type="button" data-testid="grand-livre-imprimer" onClick={() => window.print()} className="flex h-10 items-center gap-2 bg-slate-900 px-4 text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700">
              <Printer size={15} /> Imprimer
            </button>
            <button type="button" data-testid="grand-livre-close" onClick={onClose} className="border border-slate-300 p-2 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 py-6" data-testid="grand-livre-doc">
          <div className="flex items-start justify-between gap-4 border-b-2 border-slate-900 pb-3">
            <div className="flex items-center gap-3">
              {b.logo && <img src={b.logo} alt="Logo" className="h-16 w-16 object-contain" />}
              <div>
                <p className="font-heading text-lg font-black tracking-tight text-slate-900">{b.nom}</p>
                <p className="text-[11px] text-slate-600">{b.adresse}</p>
                <p className="text-[11px] text-slate-600">{b.tel} · {b.email}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading text-base font-black uppercase tracking-[0.1em] text-slate-900">Relevé de compte</p>
              <p className="font-data text-[11px] text-slate-600">Édité le {new Date().toISOString().slice(0, 10)}</p>
            </div>
            {lien && <QrLien url={lien} legende="Scannez pour votre espace agriculteur" taille={88} />}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            {compte.periode && (
              <p className="col-span-2 font-data text-[11px] uppercase tracking-[0.08em] text-slate-500">
                Période : {compte.periode}
              </p>
            )}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Contact</p>
              <p className="font-heading text-sm font-bold text-slate-900">{libelleTiers}</p>
              {compte.adresse && <p className="text-[11px] text-slate-700">{compte.adresse}</p>}
              {compte.tel && <p className="font-data text-[11px] text-slate-700">{compte.tel}</p>}
              {compte.mf && <p className="font-data text-[11px] text-slate-700">MF : {compte.mf}</p>}
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Solde net global (hors CCA)</p>
              <p data-testid="grand-livre-solde-net" className="tabular font-data text-xl font-semibold text-slate-900">{fmt(Math.abs(solde), 3)} TND</p>
              <p className="text-[11px] font-semibold text-slate-700">
                {solde > 0
                  ? 'L’huilerie doit ce montant au tiers'
                  : solde < 0
                    ? 'Le tiers doit ce montant à l’huilerie'
                    : 'Compte soldé — aucune compensation nécessaire'}
              </p>
              <p className="mt-0.5 font-data text-[10px] uppercase tracking-[0.08em] text-slate-500">
                Le compte courant d’associé est exclu de ce net — voir le bloc dédié
              </p>
              {compte.mixte && (
                <p className="mt-1 border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-data text-[10px] uppercase tracking-[0.08em] text-emerald-800">
                  Profil mixte — relevé intégré (achats + prestations)
                </p>
              )}
            </div>
          </div>

          <SoldesSepares compte={compte} />

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 print:hidden">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Période affichée {(du || au) ? `${du || '…'} → ${au || '…'}` : '(tout)'}</p>
            <PeriodeFiltre du={du} au={au} setDu={setDu} setAu={setAu} testid="periode-contact" />
          </div>
          {lots.length > 0 && (
            <div data-testid="lots-fournisseur" className="mt-4 border-2 border-dashed border-amber-400 bg-amber-50/40 p-3 print:hidden">
              <p className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-900">
                Achats d'olives — rendement & coût de revient
                <span data-testid="bloc-interne-badge" className="border border-amber-500 bg-amber-100 px-1.5 py-0.5 font-data text-[10px] font-bold uppercase tracking-[0.08em] text-amber-900">
                  Usage interne — non imprimé, non exporté
                </span>
              </p>
              <p className="mt-0.5 text-[10px] text-amber-900">
                Rendement réel atelier, coût de revient et marge unitaire (gain ou perte par kilo par
                rapport au cours de référence) : données confidentielles de l'huilerie, jamais
                communiquées au fournisseur.
              </p>
              <table className="mt-1 w-full border-collapse">
                <thead><tr className="bg-slate-100">{['Date', 'Lot', 'Qualité', 'Mode', 'Olives (kg)', 'Payé (TND)', 'Huile (kg)', 'Rendement', 'Coût / kg huile', 'Cours réf.', 'Écart / kg huile', 'Écart / kg olives', 'Traçabilité'].map((c) => <th key={c} className={th}>{c}</th>)}</tr></thead>
                <tbody>
                  {lots.map((l, i) => (
                    <tr key={`${l.id}-${i}`} data-testid={`lot-fourn-${l.id}`}>
                      <td className={`${td} font-data`}>{l.date}</td><td className={`${td} font-data font-semibold`}>{l.id}</td><td className={td}>{l.qualite}</td><td className={td}>{l.mode}</td>
                      <td className={`${td} tabular font-data text-right`}>{fmt(l.olives)}</td>
                      <td className={`${td} tabular font-data text-right`}>{l.paye != null ? fmt(l.paye, 3) : 'en attente'}</td>
                      <td className={`${td} tabular font-data text-right`}>{l.huile ? fmt(l.huile, 1) : '—'}</td>
                      <td className={`${td} tabular font-data text-right font-semibold`}>{l.rendement != null ? `${fmt(l.rendement, 2)} %` : '—'}</td>
                      <td className={`${td} tabular font-data text-right font-semibold`}>{l.coutKg != null ? fmt(l.coutKg, 3) : '—'}</td>
                      <td className={`${td} tabular font-data text-right text-slate-600`}>{l.prixRef ? fmt(l.prixRef, 3) : '—'}</td>
                      <td data-testid={`ecart-kg-huile-${l.id}`} className={`${td} tabular font-data text-right font-bold ${couleurGain(l.ecartKgHuile, '')}`}>
                        {l.ecartKgHuile == null ? '—' : `${l.ecartKgHuile >= 0 ? '+' : '−'}${fmt(Math.abs(l.ecartKgHuile), 3)}`}
                      </td>
                      <td data-testid={`ecart-kg-olives-${l.id}`} className={`${td} tabular font-data text-right font-semibold ${couleurGain(l.ecartKgOlives, '')}`}>
                        {l.ecartKgOlives == null ? '—' : `${l.ecartKgOlives >= 0 ? '+' : '−'}${fmt(Math.abs(l.ecartKgOlives), 4)}`}
                      </td>
                      <td className={td}>
                        {l.mixe ? (
                          <span data-testid={`lot-mixe-${l.id}`} title={`Trituré dans ${l.of} avec ${l.autresLots} autre(s) lot(s) : rendement attribué au prorata du poids`} className="border border-amber-400 bg-amber-50 px-1.5 font-data text-[10px] font-bold uppercase text-amber-800">Rendement de lot mixé · {l.of}</span>
                        ) : l.of ? <span className="font-data text-[10px] text-slate-600">Lot seul · {l.of}</span> : <span className="text-[10px] text-slate-400">non trituré</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {huile && (huile.depose > 0 || huile.retraits.length > 0) && (
            <div data-testid="etat-stock-huile" className="mt-4 border border-slate-300">
              <p className="border-b border-slate-300 bg-slate-100 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-900">
                État du stock d'huile à l'huilerie
              </p>
              <div className="grid grid-cols-3 divide-x divide-slate-200 border-b border-slate-200">
                <div className="px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Quantité triturée déposée</p>
                  <p data-testid="stock-huile-depose" className="tabular font-data text-base font-semibold text-slate-900">{fmt(huile.depose, 1)} kg</p>
                </div>
                <div className="px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Total retiré</p>
                  <p data-testid="stock-huile-retire" className="tabular font-data text-base font-semibold text-slate-900">{fmt(huile.retire, 1)} kg</p>
                </div>
                <div className="px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Solde disponible</p>
                  <p data-testid="stock-huile-solde" className="tabular font-data text-base font-bold text-slate-900">{fmt(huile.solde, 1)} kg</p>
                </div>
              </div>
              <table className="w-full border-collapse">
                <thead><tr className="bg-slate-50">{['Date', 'Mouvement', 'Réf.', 'Cuve', 'Quantité (kg)'].map((c) => <th key={c} className={th}>{c}</th>)}</tr></thead>
                <tbody>
                  {huile.depots.map((d) => (
                    <tr key={`d-${d.id}`} data-testid={`stock-huile-depot-${d.id}`}>
                      <td className={`${td} font-data`}>{d.date}</td>
                      <td className={td}>Dépôt après trituration</td>
                      <td className={`${td} font-data font-semibold`}>{d.id}</td>
                      <td className={td}>{d.cuve}</td>
                      <td className={`${td} tabular font-data text-right font-semibold`}>+{fmt(d.huile, 1)}</td>
                    </tr>
                  ))}
                  {huile.retraits.map((t) => (
                    <tr key={`t-${t.id}`} data-testid={`stock-huile-retrait-${t.id}`}>
                      <td className={`${td} font-data`}>{t.date}</td>
                      <td className={td}>Retrait d'huile</td>
                      <td className={`${td} font-data font-semibold`}>{t.id}</td>
                      <td className={td}>{t.cuve}</td>
                      <td className={`${td} tabular font-data text-right font-semibold`}>−{fmt(t.litres, 1)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100">
                    <td className={`${th} text-left`} colSpan={4}>Solde d'huile disponible au compte</td>
                    <td className={`${th} text-right`}>{fmt(huile.solde, 1)} kg</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <table className="mt-4 w-full border-collapse">
            <thead>
              <tr className="bg-slate-100">
                {['Date', 'Opération', 'N° Bon / Pièce', 'Brut (Kg)', 'Tare', 'Net (Kg)', 'Rdt théorique %', 'Prix base', 'Débit', 'Crédit', 'Solde', 'Notes / Justificatif'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {compte.report !== null && compte.report !== undefined && (
                <tr>
                  <td className={`${td} font-data`}>—</td>
                  <td className={td} colSpan={9}>Report du solde antérieur</td>
                  <td className={`${td} tabular font-data text-right font-semibold`}>{fmt(compte.report, 3)}</td>
                  <td className={td}>—</td>
                </tr>
              )}
              {lignesVisibles.map((l, i) => (
                <tr key={`${l.nature}-${l.id}-${i}`}>
                  <td className={`${td} font-data`}>{l.date}</td>
                  <td className={td}>{l.type}</td>
                  <td className={`${td} font-data font-semibold`}>{l.bon}</td>
                  <td className={`${td} tabular font-data text-right`}>{l.poidsBrut ? fmt(l.poidsBrut) : '—'}</td>
                  <td className={`${td} tabular font-data text-right`}>{l.poidsBrut ? fmt(l.tare) : '—'}</td>
                  <td className={`${td} tabular font-data text-right`}>{l.poidsNet ? fmt(l.poidsNet) : '—'}</td>
                  <td data-testid={`rdt-ligne-${l.nature}-${l.id}`} className={`${td} tabular font-data text-right`}>
                    {l.rendementTheorique ? fmt(l.rendementTheorique, 2) : '—'}
                  </td>
                  <td data-testid={`prix-base-${l.nature}-${l.id}`} className={`${td} tabular font-data text-right`}>
                    {l.prixBase ? (
                      <>
                        {fmt(l.prixBase, 3)}
                        <span className="ml-1 text-[9px] font-bold uppercase text-slate-500">
                          /kg {l.prixBaseUnite === 'olive' ? 'olive' : 'huile'}
                        </span>
                        {l.provisoire && (
                          <span className="block text-[9px] font-bold uppercase text-amber-800">cours figé</span>
                        )}
                        {l.prixBaseEquivalent && !l.provisoire && (
                          <span className="block text-[9px] uppercase text-slate-500">équivalent huile</span>
                        )}
                      </>
                    ) : '—'}
                  </td>
                  <td className={`${td} tabular font-data text-right ${couleurLigneTiers(l.nature, 'debit')}`}>{l.debit ? fmt(l.debit, 3) : '—'}</td>
                  <td className={`${td} tabular font-data text-right ${couleurLigneTiers(l.nature, 'credit')}`}>
                    {l.credit ? fmt(l.credit, 3) : l.provisoire ? 'en attente' : '—'}
                    {l.provisoire ? (
                      <span
                        data-testid={`badge-provisoire-${l.id}`}
                        title={`Montant provisoire calculé au cours figé${l.coursFigeValeur ? ` de ${fmt(l.coursFigeValeur, 3)} TND/kg d’huile` : ''} — prix définitif à négocier`}
                        className="mt-0.5 block border border-amber-500 bg-amber-100 px-1 text-[9px] font-black uppercase tracking-[0.06em] text-amber-900"
                      >
                        ⚠ Provisoire
                      </span>
                    ) : null}
                  </td>
                  <td className={`${td} tabular font-data text-right font-semibold`}>
                    <span className={l.provisoire ? 'border-b-2 border-dashed border-amber-500 italic' : ''}>{fmt(l.solde, 3)}</span>
                  </td>
                  <td className={`${td} text-slate-600`}>{l.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100">
                <td className={`${th} text-left`} colSpan={8}>Totaux</td>
                <td className={`${th} text-right`}>Débit {fmt(compte.totalPrestations + compte.totalPaye, 3)}</td>
                <td className={`${th} text-right`}>Crédit {fmt(compte.totalAchats + compte.totalEncaisse, 3)}</td>
                <td className={`${th} text-right`}>{fmt(solde, 3)}</td>
                <td className={th} />
              </tr>
            </tfoot>
          </table>

          <p className="mt-6 text-[10px] text-slate-500">
            Rendement indiqué : rendement théorique validé à l'entrée (mesure machine / fosse).            Les mesures de rendement réel de l'atelier ne sont pas communiquées.
          </p>
          <p className="mt-1 text-[10px] text-amber-900">
            ⚠ <span className="font-bold uppercase">Provisoire</span> : montant calculé sur le cours du
            jour figé à la réception pour les achats « à négocier ». Il n'est pas définitif et sera
            recalculé après fixation du prix convenu entre les parties.
          </p>
          <p className="mt-1 text-[10px] text-slate-500">
            Prix de base : toujours exprimé au kilo d'HUILE. Pour les lots « à négocier », il s'agit du
            cours figé à la réception qui a servi au calcul de la provision. Pour un achat à prix fixe,
            la valeur publiée est l'équivalent au kilo d'huile obtenu sur le lot. Les prestations de
            trituration sont facturées au kilo d'olives.
          </p>
          <p className="mt-1 text-[10px] text-slate-500">
            {b.mentions ?? 'Document généré automatiquement — valeurs en dinars tunisiens (TND).'}
          </p>
        </div>
      </div>

      {qrSolde && (
        <QrSoldeDialog tiersId={compte.tiersId} nom={compte.nom} tel={compte.tel} onClose={() => setQrSolde(false)} />
      )}
    </div>
  );
};
